import React, { Dispatch, useState } from 'react';
import { CoachReportWriteRateList, CoachStatChangesLast3Months } from '../../services/useFeedDashboardQueryService';
import { Col, Row } from '../../lib/utils';
import { AreaChart, ResponsiveContainer, Area, CartesianGrid, XAxis, YAxis, Tooltip } from '@mustmove/recharts';
import { MediumText, RegularText } from '../../lib/Text';
import { colors } from '../../lib/colors';
import SvgIcon from '../../components/SvgIcon';
import { CustomTooltip } from './CustomTooltip';
import FeedCoachStaticsPageRightRanking from './FeedCoachStaticsPageRightRanking';

type Props = {
    coachStatChangesLast3Months: CoachStatChangesLast3Months[];
    coachReportWriteRateList: CoachReportWriteRateList[];
    onClickAllMemberWritingRate?: () => void; // 클릭 시 전체 회원 작성률 모달 열기
    setIsMemberWritingRateModalOpen: Dispatch<React.SetStateAction<boolean>>;
};

type CoachTrendMetricType = 'reportWriteRate' | 'reRegistrationRate' | 'reRegistrationSuccessRate';

export const coachTrendMetricList: { type: CoachTrendMetricType; text: string }[] = [
    { type: 'reportWriteRate', text: '일지 작성률' },
    { type: 'reRegistrationRate', text: '재등록 회원 비중' },
    { type: 'reRegistrationSuccessRate', text: '재등록 성공률' },
];

const metricColors: Record<CoachTrendMetricType, { line: string; background: string; fill: string }> = {
    reportWriteRate: { line: '#427BE8', background: '#E6EEFF', fill: colors.primary },
    reRegistrationRate: { line: '#A32BFF', background: '#F6EAFF', fill: '#C170FF' },
    reRegistrationSuccessRate: { line: '#00A793', background: '#EAF8F4', fill: '#169873' },
};

function FeedCoachStatisticsPageCoachReportWriteRateGraph({
    coachStatChangesLast3Months,
    coachReportWriteRateList,
    onClickAllMemberWritingRate,
    setIsMemberWritingRateModalOpen,
}: Props) {
    const [threeMonthType, setThreeMonthType] = useState<CoachTrendMetricType>('reportWriteRate');

    const chartData = coachStatChangesLast3Months;
    const lineColor = metricColors[threeMonthType].line;
    const gradientionColor = lineColor;

    return (
        <Row>
            <Col className="flex w-full h-[488px]  mb-[25px] bg-white rounded-[21px] py-[26px] px-[29px] border-[0.5px] border-solid border-[#E8E8E8]">
                <Row className="flex items-center justify-between mb-[20px]">
                    <MediumText fontSize={18} lineHeight={23} color={'#4B545F'}>
                        최근 3개월 지표 변화
                    </MediumText>
                    <Row className="flex items-center ">
                        <Row className="flex items-center mr-[45px]">
                            <Col className="w-[28px] h-[2px] border-b border-dashed border-[#909090] flex-grow mr-[8px]" />
                            <RegularText fontSize={15} lineHeight={20} color={'#6D7683'}>
                                센터 평균
                            </RegularText>
                        </Row>
                        <Row className="flex items-center gap-[28px]">
                            {coachTrendMetricList.map((item) => (
                                <CheckType
                                    key={item.type}
                                    type={item.type}
                                    onClick={() => setThreeMonthType(item.type)}
                                    checked={threeMonthType === item.type}
                                />
                            ))}
                        </Row>
                    </Row>
                </Row>
                <ResponsiveContainer width="100%" height={'100%'}>
                    <AreaChart data={chartData} margin={{ top: 30, right: 20, left: 0, bottom: 20 }}>
                        <defs>
                            <linearGradient id="colorRate" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={gradientionColor} stopOpacity={0.5} />
                                <stop offset="100%" stopColor={gradientionColor} stopOpacity={0.1} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} color="#E8E8E8" strokeOpacity={0.5} strokeWidth={0.5} />
                        <XAxis
                            dataKey="monthString"
                            tickLine={false}
                            axisLine={true}
                            interval={0}
                            tick={({ x, y, payload }) => {
                                const textHeight = 24;

                                return (
                                    <g transform={`translate(${x},${y + 8})`}>
                                        <text
                                            x={0}
                                            y={0}
                                            dy={textHeight / 2 + 6}
                                            textAnchor="middle"
                                            fill="#4B545F"
                                            fontSize={12}
                                            fontWeight={400}
                                        >
                                            {payload.value}
                                        </text>
                                    </g>
                                );
                            }}
                            stroke="#E5E5E5"
                        />
                        <YAxis
                            domain={[0, 100]}
                            ticks={[0, 25, 50, 75, 100]}
                            tickFormatter={(value) => `${value}%`}
                            axisLine={false}
                            tickLine={false}
                            tickMargin={17}
                        />
                        {/* CustomTooltip */}
                        <Tooltip
                            content={({ active, payload, coordinate }) => {
                                const mainPayload = payload.find((p) => p.dataKey === threeMonthType);
                                if (!active || !mainPayload) return null;

                                return (
                                    <CustomTooltip
                                        active={true}
                                        payload={[mainPayload]}
                                        coordinate={coordinate}
                                        isMouseYRef={false}
                                    />
                                );
                            }}
                        />

                        {/* ⛳ 동적 센터 평균 점선 */}
                        <Area
                            type="monotone"
                            dataKey={
                                `center${threeMonthType.charAt(0).toUpperCase()}${threeMonthType.slice(
                                    1
                                )}` as keyof CoachStatChangesLast3Months
                            }
                            stroke="#C8CCD1"
                            strokeWidth={1.5}
                            strokeDasharray="5 5"
                            fill="none"
                            dot={<></>}
                            activeDot={false}
                        />

                        {/* ⛳ 동적 메인 지표 그래프 */}
                        <Area
                            type="monotone"
                            dataKey={threeMonthType}
                            stroke={lineColor}
                            fill="url(#colorRate)"
                            activeDot={{ r: 6 }}
                            dot={<CustomDot type={threeMonthType} stroke={lineColor} />}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </Col>
            <FeedCoachStaticsPageRightRanking
                coachReportWriteRateList={coachReportWriteRateList}
                onClickAllMemberWritingRate={() => {
                    setIsMemberWritingRateModalOpen(true);
                }}
            />
        </Row>
    );
}

export default FeedCoachStatisticsPageCoachReportWriteRateGraph;

const CustomDot = ({
    cx,
    cy,
    payload,
    type,
    stroke = '#427BE8',
}: {
    cx?: number;
    cy?: number;
    payload?: any;
    type?: CoachTrendMetricType;
    stroke?: string;
}) => {
    if (cx === undefined || cy === undefined) return null;

    const metricType = type ?? 'reportWriteRate';
    const Icon: any =
        metricType === 'reportWriteRate'
            ? 'SvgCenterStatisticsFeedWritingPercent'
            : metricType === 'reRegistrationSuccessRate'
            ? 'SvgCenterStatisticsReregisterSuccess'
            : 'SvgCenterStatisticsReregister';
    const { background: bgColor, fill: fillColor } = metricColors[metricType];

    return (
        <g>
            <circle
                cx={cx}
                cy={cy}
                r={16}
                fill={bgColor}
                stroke={stroke}
                strokeWidth={0.5}
                opacity={1}
                fillOpacity={1} // <-- controls circle translucency only
                strokeOpacity={0.5} // <-- stroke stays fully opaque
            />
            <SvgIcon name={Icon} size={16} x={cx - 8} y={cy - 8} opacity={1} fill={fillColor} />
            {/* 선명하게 */}

            {/* <LikeIcon x={cx - 8} y={cy - 30} width={16} height={16} /> */}
        </g>
    );
};

const CheckType = ({
    type,
    onClick,
    checked = false,
}: {
    type: CoachTrendMetricType;
    onClick: () => void;
    checked?: boolean;
}) => {
    const text = coachTrendMetricList.find((metric) => metric.type === type)?.text ?? '';
    const onChange = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        onClick();
    };

    return (
        <Row onClick={onClick} className="flex items-center cursor-pointer">
            <label className={`relative inline-flex items-center cursor-pointer `}>
                <div
                    onClick={onChange}
                    className={`w-[16px] h-[16px]  rounded-md flex items-center justify-center mr-[9px] ${
                        checked ? 'bg-black' : 'border-[2px] border-[#D2D8DE] border-solid'
                    }`}
                    style={{
                        width: 17,
                        height: 17,
                    }}
                >
                    {checked ? (
                        <svg
                            className=" text-primary"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                xmlns="http://www.w3.org/2000/svg"
                                d="M5 11L10.6667 16.6666L20 6.66663"
                                stroke="#FFFFFF"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    ) : (
                        <div className="w-[15px] h-[15px]" />
                    )}
                </div>
            </label>
            <RegularText fontSize={15} lineHeight={20} color={'#6D7683'} letterSpacing={-0.5}>
                {text}
            </RegularText>
        </Row>
    );
};
